import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { Sidebar } from '@treams/treams-ui';
import { DocumentIcon } from '@treams/treams-icons';

import * as XLSX from 'xlsx';
import moment from 'moment';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { useEffect, useState } from 'react';

function DashboardDownloadFeedbackProgressDepartment(props: {
	filters: graphql.AnalyticsFilters;
	onCompleted: (summary: graphql.AnalyticsFeedbackSummary[]) => void;
}) {
	const { data } = graphql.useDepartmentFeedbackSummaryQuery({
		variables: { filters: props.filters },
	});

	useEffect(() => {
		if (!data) {
			return;
		}
		props.onCompleted(data?.groupAnalytics.feedbackSummary as graphql.AnalyticsFeedbackSummary[]);
	}, [!!data]);

	return null;
}

function DashboardDownloadFeedbackProgressOrganization(props: {
	filters: graphql.AnalyticsFilters;
	onCompleted: (summary: graphql.AnalyticsFeedbackSummary[]) => void;
}) {
	const { data } = graphql.useOrganizationFeedbackSummaryQuery({
		variables: { filters: props.filters },
	});

	useEffect(() => {
		if (!data) {
			return;
		}
		props.onCompleted(
			data?.workspaceAnalytics.feedbackSummary as graphql.AnalyticsFeedbackSummary[],
		);
	}, [!!data]);

	return null;
}

export default function DashboardDownloadFeedback(props: { filters: graphql.AnalyticsFilters }) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const [isDownloading, setIsDownloading] = useState(false);

	const download = (data: graphql.AnalyticsFeedbackSummary[]) => {
		const sortedData = _.sortBy(data, [(row) => row.user.firstName, (row) => row.user.lastName]);

		const sharedHeaders = [
			t('dashboard.export.feedback.columns.firstName'),
			t('dashboard.export.feedback.columns.lastName'),
			t('dashboard.export.feedback.columns.department'),
			t('dashboard.export.feedback.columns.jobTitles'),
			t('dashboard.export.feedback.columns.username'),
		];

		const cumulativeRows = [
			[
				...sharedHeaders,
				t('dashboard.export.feedback.columns.requestsSent'),
				t('dashboard.export.feedback.columns.feedbackGivers'),
				t('dashboard.export.feedback.columns.feedbackGiversAnswered'),
				t('dashboard.export.feedback.columns.percentageAnswered'),
			],
			...sortedData.map((row) => [
				row.user.firstName,
				row.user.lastName,
				row.department.name,
				row.jobTitles.length > 0
					? row.jobTitles.map((jobTitle) => localize(jobTitle.title).text).join(', ')
					: '-',
				row.user.username,
				row.sentRequestCount,
				row.sentResponseCount,
				row.sentResponseSharedCount,
				`${
					row.sentResponseCount > 0
						? Math.round((row.sentResponseSharedCount / row.sentResponseCount) * 100)
						: 0
				}%`,
			]),
		];

		const specificRows = [
			[
				...sharedHeaders,
				t('dashboard.export.feedback.columns.requestTitle'),
				t('dashboard.export.feedback.columns.requestDate'),
				t('dashboard.export.feedback.columns.feedbackGivers'),
				t('dashboard.export.feedback.columns.feedbackGiversAnswered'),
				t('dashboard.export.feedback.columns.percentageAnswered'),
			],
			...sortedData.flatMap((row) => {
				const rows: (Date | string | number | null)[][] = [
					[
						row.user.firstName,
						row.user.lastName,
						row.department.name,
						row.jobTitles.length > 0
							? row.jobTitles.map((jobTitle) => localize(jobTitle.title).text).join(', ')
							: '-',
						row.user.username,
						row.sentRequests[0]?.title ?? '-',
						row.sentRequests[0]?.createdAt ? new Date(row.sentRequests[0]?.createdAt) : '-',
						row.sentRequests[0]?.respondentCount ?? '-',
						row.sentRequests[0]?.respondentSharedCount ?? '-',
						`${
							(row.sentRequests[0]?.respondentCount ?? 0) > 0
								? Math.round(
										(row.sentRequests[0]?.respondentSharedCount /
											row.sentRequests[0]?.respondentCount) *
											100,
								  )
								: 0
						}%`,
					],
				];

				if (row.sentRequests.length > 1) {
					for (const request of row.sentRequests.slice(1)) {
						rows.push([
							null,
							null,
							null,
							null,
							null,
							request.title,
							new Date(request.createdAt),
							request.respondentCount,
							request.respondentSharedCount,
							`${
								request.respondentCount > 0
									? Math.round((request.respondentSharedCount / request.respondentCount) * 100)
									: 0
							}%`,
						]);
					}
				}

				return rows;
			}),
		];

		const receivedRows = [
			[
				...sharedHeaders,
				t('dashboard.export.feedback.columns.requestsReceived'),
				t('dashboard.export.feedback.columns.requestsAnswered'),
				t('dashboard.export.feedback.columns.uniqueSenders'),
				t('dashboard.export.feedback.columns.percentageAnswered'),
			],
			...sortedData.map((row) => [
				row.user.firstName,
				row.user.lastName,
				row.department.name,
				row.jobTitles.length > 0
					? row.jobTitles.map((jobTitle) => localize(jobTitle.title).text).join(', ')
					: '-',
				row.user.username,
				row.receivedRequestCount,
				row.receivedRequestSharedCount,
				row.uniqueRequestorCount,
				`${
					row.receivedRequestCount > 0
						? Math.round((row.receivedRequestSharedCount / row.receivedRequestCount) * 100)
						: 0
				}%`,
			]),
		];

		const workbook = XLSX.utils.book_new();

		const defaultColumnOptions: XLSX.ColInfo[] = [
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 30 },
			{ wch: 15 },
		];

		const cumulativeWorksheet = XLSX.utils.aoa_to_sheet(cumulativeRows);

		cumulativeWorksheet['!cols'] = defaultColumnOptions;

		XLSX.utils.book_append_sheet(
			workbook,
			cumulativeWorksheet,
			t('dashboard.export.feedback.tabs.cumulative'),
		);

		const specificWorksheet = XLSX.utils.aoa_to_sheet(specificRows);

		specificWorksheet['!cols'] = defaultColumnOptions;

		XLSX.utils.book_append_sheet(
			workbook,
			specificWorksheet,
			t('dashboard.export.feedback.tabs.specific'),
		);

		const receivedWorksheet = XLSX.utils.aoa_to_sheet(receivedRows);

		receivedWorksheet['!cols'] = defaultColumnOptions;

		XLSX.utils.book_append_sheet(
			workbook,
			receivedWorksheet,
			t('dashboard.export.feedback.tabs.received'),
		);

		const startDate = moment(props.filters.startDate).format('DD-MM-YYYY');
		const endDate = moment(props.filters.endDate).format('DD-MM-YYYY');

		XLSX.writeFile(workbook, `Feedback export ${startDate} - ${endDate}.xlsx`);
	};

	// Call download() with data from the graphql call
	return (
		<>
			{isDownloading &&
				(props.filters.groupIDs && props.filters.groupIDs.length > 0 ? (
					<DashboardDownloadFeedbackProgressDepartment
						filters={props.filters}
						onCompleted={(summary) => {
							download(summary);
							setIsDownloading(false);
						}}
					/>
				) : (
					<DashboardDownloadFeedbackProgressOrganization
						filters={props.filters}
						onCompleted={(summary) => {
							download(summary);
							setIsDownloading(false);
						}}
					/>
				))}
			<Sidebar.Item
				icon={<DocumentIcon />}
				title={t('dashboard.sidebar.downloads.feedbackRequests.title')}
				onClick={() => {
					setIsDownloading(true);
				}}
				small
				loading={isDownloading}
			/>
		</>
	);
}
